import { SvgImageTypes } from '../components/CapabilitiesIntroDetail';
import { SizeText } from '../components/Case';
import { TitleAndTextInRowsSize } from '../components/TitleAndTextInRows';
import {
  Image,
  Link,
  LinkStyle,
  TitleDescriptionFormat,
  LogosFormat,
  BenefitsFormat,
  TeamMembersFormat,
  SingleImageContainerFormat,
  MultipleImageContainerFormat,
  CaseFormat,
  TEXTPOSITION,
  SUBTITLESIZE,
  TITLESIZE,
  IMAGESIZECONTAINER,
} from './common';

export interface Meta {
  title: string | null;
  description: string | null;
  keywords: string | null;
  image?: {
    src: string | null;
    alt: string | null;
  };
}

export enum BlockTypes {
  Claim = 'blockClaim',
  LatestProject = 'blockLatestProject',
  OurSolutionsWithLink = 'blockOurSolutionsWithLink',
  ImageTitleTextAndLink = 'blockImageTitleTextAndLink',
  Highlights = 'blockHighlight',
  OpenPositions = 'blockOpenPositionsLink',
  CapabilitiesHero = 'blockCapabilitiesHero',
  ServiceColumns = 'blockServiceColumns',
  OurValues = 'blockOurValues',
  PartnerRelationships = 'blockPartnerRelationships',
  CapabilitiesIntroDetail = 'blockCapabilityDetailTop',
  TitleAndTextInColumns = 'blockTitleAndTextInColumns',
  ServicesSolutions = 'blockServicesAndSolutions',
  Benefits = 'blockBenefits',
  Case = 'blockCase',
  Next = 'blockNext',
  Related = 'blockRelated',
  TitleAndTextInRows = 'blockTitleAndTextInRows',
  MeetUs = 'blockMeetUs',
  OpenPositionsList = 'blockOpenPositionsList',
  Accordion = 'blockAccordion',
  Team = 'blockTeamMembersList',
  ContactUs = 'blockContactUsForm',
  FormProject = 'blockGetEstimateForm',
  SingleImageContainer = 'blockSingleImageContainer',
  MultipleImageContainer = 'blockMultipleImageContainer',
  WorkDetailIntro = 'blockProjectIntroWithDeliverables',
  CaseContainer = 'blockCaseContainer',
  CareerDetail = 'blockCareerDetail',
  Author = 'blockAuthor',
  ProjectDetails = 'blockProjectDetails',
  Video = 'blockMediaHero',
  Code = 'blockCodeSnippet',
  ProjectsList = 'blockProjectsList',
  BlogArticlesList = 'blockBlogArticlesList',
}

export interface BlockClaim {
  id: number;
  type: BlockTypes.Claim;
  slug: string;
  text: string;
}

export interface BlockLatestProject {
  id: number;
  type: BlockTypes.LatestProject;
  slug: string;
  title: string;
  description: string;
  link: Link;
  image: Image;
  isImageFirst: boolean;
}

interface BlockOurSolutionsWithLink {
  id: number;
  type: BlockTypes.OurSolutionsWithLink;
  slug: string;
  title: string;
  description: string;
  textServices: string;
  link: Link;
  isList: boolean;
  services: Link[];
  isCentered: boolean;
}

interface BlockImageTitleTextAndLink {
  id: number;
  type: BlockTypes.ImageTitleTextAndLink | BlockTypes.BlogArticlesList;
  slug: string;
  title: string;
  description: string;
  link: Link;
  image: Image;
  isImageFirst: string;
}

export interface BlockHighLight {
  id: number;
  type: BlockTypes.Highlights;
  slug: string;
  title: string;
  description: string;
  link: Link;
  image: Image;
  keywords: string[];
}

interface BlockOpenPositions {
  id: number;
  type: BlockTypes.OpenPositions;
  slug: string;
  title: string;
  link: Link;
}

interface BlockCapabilitiesHero {
  id: number;
  type: BlockTypes.CapabilitiesHero;
  slug: string;
  title: string;
  image: Image;
  isImageFirst: boolean;
  messages: TitleDescriptionFormat[];
}

interface BlockServiceColumns {
  id: number;
  type: BlockTypes.ServiceColumns;
  slug: string;
  indexNr: string;
  title: string;
  descriptionTitle: string;
  descriptionText: string;
  image: Image;
  isImageFirst: boolean;
  imgLink: Link;
  links: Link[];
}

interface BlockOurValues {
  id: number;
  type: BlockTypes.OurValues;
  slug: string;
  title: string;
  messages: TitleDescriptionFormat[];
}

interface BlockPartnerRelationships {
  id: number;
  type: BlockTypes.PartnerRelationships;
  slug: string;
  title: string;
  logos: Array<LogosFormat>[];
}

interface BlockCapabilitiesIntroDetail {
  id: number;
  type: BlockTypes.CapabilitiesIntroDetail;
  slug: string;
  title: string;
  subTitle: string;
  extraInfo: string;
  svgImageType: SvgImageTypes;
}

interface BlockTitleAndTextInColumns {
  id: number;
  type: BlockTypes.TitleAndTextInColumns;
  slug: string;
  title: string;
  text: string;
}

interface BlockServicesSolutions {
  id: number;
  type: BlockTypes.ServicesSolutions;
  slug: string;
  title: string;
  services: string[];
}

interface BlockBenefits {
  id: number;
  type: BlockTypes.Benefits;
  slug: string;
  title: string;
  benefits: BenefitsFormat[];
}

interface BlockCase {
  id: number;
  type: BlockTypes.Case;
  slug: string;
  title: string;
  titleTextSize: SizeText;
  image: Image;
  link: Link;
}

interface BlockNext {
  id: number;
  type: BlockTypes.Next;
  slug: string;
  title: string;
  link: Link;
}

interface BlockRelated {
  id: number;
  type: BlockTypes.Related;
  slug: string;
  title: string;
  text: string;
  isTextOnLeft: boolean;
  links: Link[];
}

export interface BlockTitleAndTextInRowsProps {
  titleSize?: TITLESIZE;
  textSize?: SUBTITLESIZE;
  textPosition?: TEXTPOSITION;
  blockSize?: TitleAndTextInRowsSize;
  delay?: boolean;
  titleAnimated?: boolean;
  animated?: boolean;
}

export type BlockTitleAndTextInRows = {
  id: number;
  type: BlockTypes.TitleAndTextInRows;
  slug: string;
  title?: string;
  text?: string;
} & BlockTitleAndTextInRowsProps;

interface BlockMeetUs {
  id: number;
  type: BlockTypes.MeetUs;
  slug: string;
  title: string;
  description: string;
  link: Link;
  image: Image;
}

export interface OpenPosition {
  id: number;
  title: string;
  url: string;
  style?: LinkStyle;
  languages: Languages;
}

interface BlockOpenPositionsList {
  id: number;
  type: BlockTypes.OpenPositionsList;
  slug: string;
  title: string;
  openPositions: OpenPosition[];
}

interface BlockAccordion {
  id: number;
  type: BlockTypes.Accordion;
  slug: string;
  title: string;
  messages: TitleDescriptionFormat[];
}

interface BlockTeamMembersList {
  id: number;
  type: BlockTypes.Team;
  slug: string;
  data: TeamMembersFormat[];
}

interface BlockContactUsForm {
  id: number;
  type: BlockTypes.ContactUs;
  slug: string;
  title: string;
}

interface BlockGetEstimateForm {
  id: number;
  type: BlockTypes.FormProject;
  slug: string;
  title: string;
}

interface BlockSingleImageContainer extends SingleImageContainerFormat {
  id: number;
  type: BlockTypes.SingleImageContainer;
}

export interface BlockMultipleImageContainer {
  id: number;
  type: BlockTypes.MultipleImageContainer;
  size: IMAGESIZECONTAINER;
  images: (Video | Image | undefined)[];
}

interface BlockWorkDetailIntro {
  id: number;
  type: BlockTypes.WorkDetailIntro;
  deliverableTitle: string;
  deliverables: string[];
  intro: string;
  title: string;
  description: string;
}

interface BlockCaseContainer {
  id: number;
  type: BlockTypes.CaseContainer;
  data: CaseFormat[];
}

interface BlockCareerDetail {
  id: number;
  type: BlockTypes.CareerDetail;
  title: Link;
  details: TitleDescriptionFormat[];
  specs: TitleDescriptionFormat[];
  link: Link;
  linkDescription: string;
}

interface BlockAuthor {
  id: number;
  type: BlockTypes.Author;
  title: string;
  subTitle: string;
  image: Image;
  link: Link;
  containerPosition: TEXTPOSITION;
}

interface BlockWorkKeywords {
  id: number;
  type: BlockTypes.ProjectDetails;
  link: Link;
  keywords: TitleDescriptionFormat[];
}

export interface Video {
  src: string;
  alt: string;
  type?: string;
}

interface BlockVideo {
  id: number;
  type: BlockTypes.Video;
  video: Video;
  image?: Image;
  video_auto_play?: boolean;
  video_muted?: boolean;
  video_show_controls?: boolean;
}

interface BlockCode {
  id: number;
  type: BlockTypes.Code;
  code: string;
}

interface BlockProjectsList {
  id: number;
  type: BlockTypes.ProjectsList;
  style: Link;
}

export interface BlockProject {
  id?: number;
  title: string;
  keywords: string[];
  url: string;
  type?: 'work' | 'case';
  languages?: Languages;
  image: Image;
}

export interface ProjectsResponseMeta {
  current_page: number;
  from: number;
  last_page: number;
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export type BlogResponseMeta = ProjectsResponseMeta;

export interface ProjectsResponse {
  data: BlockProject[];
  meta: ProjectsResponseMeta;
}

export interface BlockBlogArticle {
  id?: number;
  title: string;
  description: string;
  isImageFirst: boolean;
  url: string;
  type?: 'work' | 'case';
  languages?: Languages;
  image: Image;
}

export interface BlogResponse {
  data: BlockBlogArticle[];
  meta: ProjectsResponseMeta;
}

export type Blocks = (
  | BlockClaim
  | BlockLatestProject
  | BlockOurSolutionsWithLink
  | BlockImageTitleTextAndLink
  | BlockHighLight
  | BlockOpenPositions
  | BlockCapabilitiesHero
  | BlockServiceColumns
  | BlockOurValues
  | BlockPartnerRelationships
  | BlockCapabilitiesIntroDetail
  | BlockTitleAndTextInColumns
  | BlockServicesSolutions
  | BlockBenefits
  | BlockCase
  | BlockNext
  | BlockRelated
  | BlockTitleAndTextInRows
  | BlockMeetUs
  | BlockOpenPositionsList
  | BlockAccordion
  | BlockTeamMembersList
  | BlockContactUsForm
  | BlockGetEstimateForm
  | BlockSingleImageContainer
  | BlockMultipleImageContainer
  | BlockWorkDetailIntro
  | BlockCaseContainer
  | BlockCareerDetail
  | BlockAuthor
  | BlockWorkKeywords
  | BlockVideo
  | BlockCode
  | BlockProjectsList
)[];

export interface Languages {
  [id: string]: { language_id: string; slug: string; url: string };
}

export interface Block {
  id: number;
  type: BlockTypes;
  slug: string;
  title: string;
  description: string;
  link: Link;
  image: Image;
  isImageFirst: boolean;
}

export interface PageResponse {
  id: number;
  title: string;
  slug: string;
  language_id: string;
  url: string;
  blocks: Blocks;
  meta: Meta;
  details?: TitleDescriptionFormat[];
  specs?: TitleDescriptionFormat[];
  link?: Link;
  linkDescription?: string;
  errorCode?: string;
  content?: string;
  languages?: Languages;
}
